// Builds a compact, human-readable snapshot of the SMG site content
// so Jarvis can answer with up-to-the-minute copy. Regenerated on every send —
// edit any data file and Jarvis sees it on the next chat turn.

import { caseStudies, caseStudyOrder } from '../data/caseStudies';
import {
  capabilities,
  coBrandTiers,
  engineSteps,
  clients,
  studioInfo,
  praise,
  contactInfo,
  heroStats,
} from '../data/mock';

const clip = (s, max = 320) => {
  if (!s) return '';
  const t = String(s).replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

const isPlaceholder = (s) => typeof s === 'string' && s.trim().startsWith('[');

export function buildSiteContext() {
  const lines = [];

  // Studio
  lines.push('STUDIO');
  lines.push(`- Founder: ${studioInfo.name} (${studioInfo.title}) — ${studioInfo.role}`);
  lines.push(`- Based: ${studioInfo.based}`);
  lines.push(`- Focus: ${studioInfo.focus}`);
  lines.push(`- Founder quote: "${studioInfo.quote}"`);

  // Contact
  lines.push('\nCONTACT');
  lines.push(`- Public: ${contactInfo.email}`);
  lines.push(`- Press: ${contactInfo.press}`);
  contactInfo.studios.forEach((s) => {
    lines.push(`- ${s.city} — ${s.dept} · ${s.phone}`);
  });

  // Hero stats
  lines.push('\nSTUDIO STATS');
  heroStats.forEach((h) => lines.push(`- ${h.value} — ${h.label}`));

  // Capabilities
  lines.push('\nCAPABILITIES');
  capabilities.forEach((c) => {
    lines.push(`${c.num}. ${c.title} — ${clip(c.desc, 180)}`);
  });

  // Co-brand tiers
  lines.push('\nCO-BRAND TIERS');
  coBrandTiers.forEach((t) => {
    lines.push(`- ${t.title} (${t.range}) — ${clip(t.desc, 160)}`);
  });

  // Engine steps
  lines.push('\nCO-BRAND ENGINE STEPS');
  engineSteps.forEach((s) => lines.push(`- ${s.num} ${s.title} ${clip(s.desc, 80)}`));

  // Clients (short)
  lines.push('\nCLIENTS ON THE SITE');
  clients.forEach((c) => lines.push(`- ${c.name} — ${c.tag}`));

  // Praise
  lines.push('\nPRAISE');
  lines.push(`"${praise.quote}" — ${praise.author}, ${praise.role}`);

  // Case studies (live)
  lines.push('\nCASE STUDIES (deep pages at /work/:slug)');
  caseStudyOrder.forEach((slug) => {
    const cs = caseStudies[slug];
    if (!cs) return;
    lines.push(`\n# ${cs.client} — /work/${cs.slug}`);
    lines.push(`- Title: ${cs.title}`);
    lines.push(`- Category: ${cs.category} · ${cs.year}`);
    if (cs.tagline && !isPlaceholder(cs.tagline)) {
      lines.push(`- Tagline: ${clip(cs.tagline, 240)}`);
    }
    if (cs.brief && !isPlaceholder(cs.brief)) {
      lines.push(`- Brief: ${clip(cs.brief, 420)}`);
    }
    if (cs.approach && !isPlaceholder(cs.approach)) {
      lines.push(`- Approach: ${clip(cs.approach, 420)}`);
    }
    if (Array.isArray(cs.deliverables) && cs.deliverables.length) {
      const items = cs.deliverables
        .map((d) => (typeof d === 'object' ? d.title || d.desc : d))
        .filter(Boolean)
        .map((t) => clip(t, 90));
      if (items.length) lines.push(`- Deliverables: ${items.join(' · ')}`);
    }
    if (Array.isArray(cs.results) && cs.results.length) {
      const stats = cs.results
        .filter((r) => r?.value && !String(r.value).includes('['))
        .map((r) => `${r.value} ${r.label}`);
      if (stats.length) lines.push(`- Impact: ${stats.join(' · ')}`);
    }
    if (cs.highlights?.items?.length) {
      lines.push(`- Highlights: ${cs.highlights.items.join(' · ')}`);
    }
    if (cs.testimonial?.quote && !isPlaceholder(cs.testimonial.quote)) {
      lines.push(
        `- Testimonial: "${clip(cs.testimonial.quote, 220)}" — ${cs.testimonial.author}, ${cs.testimonial.role}`,
      );
    }
  });

  return lines.join('\n');
}
